import { useEffect, useRef, useState } from "react";
import { MoveHorizontal } from "lucide-react";

// Draggable before/after comparison — gallery pairs, the job note's Before & after section, the
// customer portal and the fullscreen viewer. Hardened: the slider only appears once BOTH photos
// have actually loaded (a stuck "after" used to leave the before showing on both sides); each
// photo gets one silent cache-busted retry; a photo that still fails drops the card (onError) or
// falls back to the plain before photo.
// On the page (onOpen given) a finger never drags: swipes scroll the page or strip and a tap opens
// the fullscreen viewer. In the viewer (dragAnywhere) any one-finger drag moves the slider.
// A mouse drags the handle (or anywhere unless handleOnly); a click opens the viewer.
export const BeforeAfter = ({ beforeSrc, afterSrc, caption, testid, onError, tall = false, fill = false, onOpen, handleOnly = false, cover = false, dragAnywhere = false }) => {
  const ref = useRef(null);
  const drag = useRef(null); // pointerId doing the dragging
  const press = useRef(null); // { id, x, y, t, mouse }
  const [pos, setPos] = useState(50);
  const [beforeSt, setBeforeSt] = useState("loading"); // loading | retry | ready | failed
  const [afterSt, setAfterSt] = useState("loading");

  useEffect(() => {
    if (beforeSt === "failed" || afterSt === "failed") onError?.();
  }, [beforeSt, afterSt]); // eslint-disable-line react-hooks/exhaustive-deps

  const move = (clientX) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    setPos(Math.min(96, Math.max(4, ((clientX - r.left) / r.width) * 100)));
  };
  const bust = (src) => `${src}${src.includes("?") ? "&" : "?"}retry=1`;
  const onImgError = (st, setSt) => () => setSt(st === "loading" ? "retry" : "failed");
  const startDrag = (e) => {
    drag.current = e.pointerId;
    press.current = null;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* pointer already gone */ }
  };

  const onDown = (e) => {
    if (dragAnywhere) {
      e.stopPropagation(); // the viewer never swipes or zooms a pair — the drag is the slider's
      if (drag.current !== null) return; // a second finger doesn't take over
      startDrag(e);
      move(e.clientX);
      return;
    }
    press.current = { id: e.pointerId, x: e.clientX, y: e.clientY, t: Date.now(), mouse: e.pointerType === "mouse" };
  };
  const onMove = (e) => {
    if (drag.current !== null) { if (e.pointerId === drag.current) move(e.clientX); return; }
    const p = press.current;
    if (p && p.id === e.pointerId && p.mouse && !handleOnly && e.buttons && Math.abs(e.clientX - p.x) > 4) {
      startDrag(e);
      move(e.clientX);
    }
  };
  const onUp = (e) => {
    const p = press.current;
    press.current = null;
    if (drag.current !== null) { if (e.pointerId === drag.current) drag.current = null; return; }
    if (p && onOpen && p.id === e.pointerId && Math.hypot(e.clientX - p.x, e.clientY - p.y) < 10 && Date.now() - p.t < 700) onOpen();
  };
  const onCancel = (e) => {
    press.current = null;
    if (e.pointerId === drag.current) drag.current = null;
  };
  const grab = (e) => {
    if (dragAnywhere || (onOpen && e.pointerType !== "mouse")) return; // container handles it (drag / tap to open)
    e.stopPropagation(); // the handle is never a tap-to-open or a viewer swipe
    startDrag(e);
  };
  const nudge = (e) => {
    if (e.key === "ArrowLeft") { e.preventDefault(); setPos((v) => Math.max(4, v - 5)); }
    if (e.key === "ArrowRight") { e.preventDefault(); setPos((v) => Math.min(96, v + 5)); }
  };

  const ready = beforeSt === "ready" && afterSt === "ready";

  // No onError (fullscreen) → never show a one-sided slider, show the before photo plain
  if (afterSt === "failed" && !onError) {
    return (
      <img src={beforeSrc} alt={caption ? `${caption} — before` : "Before"} draggable={false}
        data-testid={`${testid}-single`} className={`w-full ${fill ? "h-full" : "max-h-[78dvh]"} object-contain`} />
    );
  }

  const sizeCls = fill ? "h-full" : tall ? "h-[78dvh]" : "aspect-[4/3]";
  const fit = cover ? "object-contain md:object-cover" : "object-contain"; // cover: crop portraits on desktop, no letterbox bars
  const cursor = dragAnywhere ? "cursor-ew-resize" : onOpen ? "cursor-zoom-in" : handleOnly ? "" : "cursor-ew-resize";
  const labelTop = fill ? { top: "calc(max(12px, env(safe-area-inset-top)) + 64px)" } : undefined;
  return (
    <div
      ref={ref}
      data-testid={testid}
      data-drag-anywhere={dragAnywhere ? "true" : undefined}
      className={`relative w-full ${sizeCls} overflow-hidden select-none ${dragAnywhere ? "touch-none" : "touch-manipulation"} ${fill ? "bg-black" : "bg-neutral-900"} ${cursor}`}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onCancel}
    >
      {!ready && (
        <div className="absolute inset-0 flex items-center justify-center" data-testid={`${testid}-loading`}>
          <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Loading photos…</span>
        </div>
      )}
      <img src={beforeSt === "retry" ? bust(beforeSrc) : beforeSrc} alt={caption ? `${caption} — before` : "Before"} draggable={false}
        onLoad={() => setBeforeSt("ready")} onError={onImgError(beforeSt, setBeforeSt)}
        className={`absolute inset-0 w-full h-full ${fit} pointer-events-none transition-opacity duration-200 ${ready ? "opacity-100" : "opacity-0"}`} />
      <img src={afterSt === "retry" ? bust(afterSrc) : afterSrc} alt={caption ? `${caption} — after` : "After"} draggable={false}
        onLoad={() => setAfterSt("ready")} onError={onImgError(afterSt, setAfterSt)}
        className={`absolute inset-0 w-full h-full ${fit} pointer-events-none transition-opacity duration-200 ${ready ? "opacity-100" : "opacity-0"}`}
        style={{ clipPath: `inset(0 0 0 ${pos}%)` }} />
      {ready && (
        <>
          <div
            data-testid={`${testid}-handle`}
            role="slider"
            tabIndex={0}
            aria-label="Drag to compare before and after"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(pos)}
            onPointerDown={grab}
            onKeyDown={nudge}
            className={`absolute inset-y-0 z-10 w-14 -translate-x-1/2 flex justify-center cursor-ew-resize ${onOpen || dragAnywhere ? "" : "touch-pan-y"} focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80`}
            style={{ left: `${pos}%` }}
          >
            <div className="w-0.5 h-full bg-white shadow-[0_0_8px_rgba(0,0,0,0.5)] pointer-events-none" />
            <span className={`absolute top-1/2 -translate-y-1/2 ${fill ? "h-14 w-14" : "h-11 w-11"} flex items-center justify-center bg-white text-navy rounded-full shadow-md pointer-events-none`}>
              <MoveHorizontal className={fill ? "h-6 w-6" : "h-5 w-5"} />
            </span>
          </div>
          <span className={`absolute left-2 text-[10px] font-mono uppercase tracking-wider bg-black/60 text-white px-2 py-0.5 pointer-events-none ${fill ? "" : "top-2"}`}
            style={labelTop}>Before</span>
          <span className={`absolute right-2 text-[10px] font-mono uppercase tracking-wider bg-black/60 text-white px-2 py-0.5 pointer-events-none ${fill ? "" : "top-2"}`}
            style={labelTop}>After</span>
          {onOpen && (
            <span data-testid={`${testid}-tap-hint`}
              className="hidden [@media(hover:none)]:inline-flex absolute z-20 bottom-2 left-1/2 -translate-x-1/2 items-center rounded-full bg-black/65 text-white text-[11px] font-semibold px-3 py-1 pointer-events-none whitespace-nowrap">
              Tap to compare
            </span>
          )}
        </>
      )}
    </div>
  );
};
